import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ArrowLeft, Calendar, Clock, User, Phone, FileText, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookingsPage = ({ token }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/bookings`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    setUpdating(bookingId);
    try {
      const response = await fetch(`${API}/bookings/${bookingId}/status?status=${newStatus}`, {
        method: "PATCH",
        headers
      });
      if (response.ok) {
        toast.success(`Booking ${newStatus}`);
        fetchBookings();
      } else {
        toast.error("Failed to update booking");
      }
    } catch (err) {
      toast.error("Error updating booking");
    } finally {
      setUpdating(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "confirmed": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "cancelled": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "completed": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/dashboard")}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white">Doctor Consultations</h1>
          <Badge variant="outline" className="text-emerald-400 border-emerald-500/30">
            {bookings.length} Total
          </Badge>
        </div>

        {bookings.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No bookings yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {bookings.map((booking) => (
              <Card key={booking.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <Badge className={`${getStatusColor(booking.status)} border`}>
                          {booking.status.toUpperCase()}
                        </Badge>
                        <span className="text-slate-400 text-sm">
                          {new Date(booking.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-white font-semibold text-lg">{booking.patient_name}</h3>
                          <div className="flex items-center gap-4 text-slate-400 text-sm mt-1">
                            <span>{booking.age} years, {booking.sex}</span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {booking.phone}
                            </span>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-emerald-400 font-medium">{booking.doctor_name}</p>
                          <p className="text-slate-400 text-sm">{booking.doctor_specialty}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        {booking.preferred_slot && (
                          <span className="flex items-center gap-1 text-slate-300">
                            <Clock className="w-4 h-4 text-slate-500" />
                            {booking.preferred_slot}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-slate-300">
                          <FileText className="w-4 h-4 text-slate-500" />
                          {booking.consultation_type === "teleconsult" ? "Teleconsult" : "In-Person"}
                        </span>
                      </div>
                      
                      {booking.complaints && (
                        <div className="bg-slate-700/30 rounded-lg p-3 mt-2">
                          <p className="text-slate-400 text-sm">
                            <span className="text-slate-500">Complaints: </span>
                            {booking.complaints}
                          </p>
                          {booking.provisional_diagnosis && (
                            <p className="text-slate-400 text-sm mt-1">
                              <span className="text-slate-500">Provisional Dx: </span>
                              <span className="text-amber-400">{booking.provisional_diagnosis}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {booking.status === "pending" && (
                      <div className="flex gap-2 lg:flex-col">
                        <Button
                          size="sm"
                          onClick={() => updateStatus(booking.id, "confirmed")}
                          disabled={updating === booking.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {updating === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Confirm
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(booking.id, "cancelled")}
                          disabled={updating === booking.id}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}
                    
                    {booking.status === "confirmed" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(booking.id, "completed")}
                        disabled={updating === booking.id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Mark Complete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookingsPage;
